import * as styles from "./SliderMarks.css";

import { clsx } from "clsx";
import type { JSX } from "react";

import { Slider as BaseSlider } from "@base-ui/react/slider";

import { useSliderContext } from "./Slider.context";
import type { SliderMark, SliderMarksProps } from "./Slider.types";

type ResolvedMark = { value: number; label?: React.ReactNode };

function resolveMarks(
  marks: SliderMark[] | undefined,
  min: number,
  max: number,
  step: number,
): ResolvedMark[] {
  if (marks) {
    return marks.map((mark) =>
      typeof mark === "number" ? { value: mark } : mark,
    );
  }

  const resolved: ResolvedMark[] = [];

  for (let value = min; value <= max; value += step) {
    resolved.push({ value });
  }

  return resolved;
}

export function SliderMarks({
  marks: marksProp,
  className,
  ...props
}: SliderMarksProps): JSX.Element {
  const { min, max, step, orientation } = useSliderContext();

  const marks = resolveMarks(marksProp, min, max, step);
  const edge = orientation === "vertical" ? "bottom" : "left";

  const percent = (value: number) =>
    max === min ? 0 : ((value - min) / (max - min)) * 100;

  return (
    <BaseSlider.Value
      data-slot="slider-marks"
      render={<span />}
      aria-hidden
      className={clsx(styles.marks, className)}
      {...props}
    >
      {(_, values) => {
        // A single value fills from the start, a range between its thumbs.
        const low = values.length > 1 ? Math.min(...values) : min;
        const high = Math.max(...values);

        return marks.map(({ value, label }) => {
          const active = value >= low && value <= high;
          const position = { [edge]: `${percent(value)}%` };

          return (
            <span key={value} style={{ display: "contents" }}>
              <span
                data-slot="slider-mark"
                data-active={active || undefined}
                className={styles.mark}
                style={position}
              />
              {label !== undefined && (
                <span
                  data-slot="slider-mark-label"
                  className={styles.label}
                  style={position}
                >
                  {label}
                </span>
              )}
            </span>
          );
        });
      }}
    </BaseSlider.Value>
  );
}
